import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  VirtualizedCombobox,
} from '@roger/ui-components';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import {
  listAccountMerges,
  listMergeCandidates,
  listUserMergeCandidates,
  mergeAccounts,
  mergeUsers,
  previewAccountMerge,
  previewUserMerge,
  retryAccountMergeSteps,
  retryUserMergeSteps,
  reverseAccountMerge,
} from '../../../services/api';
import type {
  AccountMergeCandidate,
  AccountMergeHistory,
  AccountMergePreview,
  AccountMergeRecord,
  AccountMergeRequest,
  AccountMergeReversal,
  AccountMergeUser,
  AgencyResponse,
} from '../../../services/api/types.gen';
import { PermissionSlug } from '../../../constants/featureFlags';
import { useStore } from '../../../store';
import { getAgencyClinicDisplayName } from '../../../utils/agencyClinicDisplayName';
import { getUserFacingApiMessage } from '../../../utils/apiErrorDetail';
import { formatDateTimeInLocalZone } from '../../../utils/dateUtils';
import { getHttpStatus } from '../../../utils/httpErrorUtils';

type ReversalSummary = Omit<AccountMergeReversal, 'merge'>;

type Step =
  | { kind: 'pick' }
  | { kind: 'preview'; preview: AccountMergePreview }
  | { kind: 'confirmReverse'; merge: AccountMergeRecord; back: Step }
  | { kind: 'done'; merge: AccountMergeRecord; reversal?: ReversalSummary };

/**
 * Internal Tools merges in any agency and lists, retries and reverses its past
 * merges; the User Manager merges in the caller's own agency only.
 */
export type AccountMergeScope =
  { agency: Pick<AgencyResponse, 'id' | 'written_name'> } | 'own-agency';

export interface MergeAccountDialogProps {
  /** The dialog is open while set. */
  scope: AccountMergeScope | null;
  /** Chosen already as the account to keep, if it can be kept. */
  keepUserId?: string;
  onClose: () => void;
  /** After a merge or a reversal changed the members. */
  onAccountsChanged: () => void;
}

// A refusal's status is read from `response` (see wasRefused).
type ApiCall<T> = Promise<{
  data?: T;
  error?: unknown;
  response?: { status?: number };
}>;

interface MergeApi {
  candidates: () => ApiCall<AccountMergeCandidate[]>;
  preview: (
    sourceUserId: string,
    targetUserId: string
  ) => ApiCall<AccountMergePreview>;
  merge: (body: AccountMergeRequest) => ApiCall<AccountMergeRecord>;
  retry: (mergeId: string) => ApiCall<AccountMergeRecord>;
  history?: () => ApiCall<AccountMergeHistory>;
  reverse?: (mergeId: string) => ApiCall<AccountMergeReversal>;
}

function agencyApi(agencyId: string): MergeApi {
  const path = { agency_id: agencyId };
  return {
    candidates: () => listMergeCandidates({ path }),
    preview: (source_user_id, target_user_id) =>
      previewAccountMerge({ path, query: { source_user_id, target_user_id } }),
    merge: body => mergeAccounts({ path, body }),
    retry: merge_id => retryAccountMergeSteps({ path: { ...path, merge_id } }),
    history: () => listAccountMerges({ path }),
    reverse: merge_id => reverseAccountMerge({ path: { ...path, merge_id } }),
  };
}

const OWN_AGENCY_API: MergeApi = {
  candidates: () => listUserMergeCandidates(),
  preview: (source_user_id, target_user_id) =>
    previewUserMerge({ query: { source_user_id, target_user_id } }),
  merge: body => mergeUsers({ body }),
  retry: merge_id => retryUserMergeSteps({ path: { merge_id } }),
};

const UNCONFIRMED =
  'The merge may not have finished. If this preview says these accounts were merged before, it did.';
const UNCONFIRMED_PICK =
  'The merge may not have finished. Check Past merges before trying again.';
const UNCONFIRMED_PICK_OWN =
  'The merge may not have finished. Preview the same two accounts before trying again.';
const FINISH_LATER_OWN =
  'If you close this now, merge the same two accounts again to finish.';
const REVERSAL_UNCONFIRMED =
  'The reversal may not have finished. Check Past merges before trying again.';
const STEPS_PENDING =
  'Moving the RogerDoc account links has not finished; retry.';

const SamePersonNote: React.FC = () => (
  <Alert className='border-amber-200 bg-amber-50'>
    <AlertDescription className='flex items-start gap-2 text-amber-900'>
      <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-amber-600' />
      <span>
        <strong>Only merge two accounts that belong to the same person.</strong>{' '}
        Merging two different people&apos;s accounts gives one person the
        other&apos;s visits, history and Kinnser logins.
      </span>
    </AlertDescription>
  </Alert>
);

const KEPT_LABELS: Record<string, string> = {
  signatures: 'Signatures',
  physician_links: 'Physician links',
  esignature: 'E-signature',
  npi: 'NPI',
};

// Rows the account holds itself and the work it did, where "table: column"
// says nothing.
const ACCOUNT_LABELS: Record<string, string> = {
  'intakes.assigned_to': 'Intakes to review',
  'group_members.user_id': 'Group memberships',
  'user_licenses.user_id': 'Licenses',
  'dashboard_tabs.user_id': 'Dashboard tabs',
  'report_widgets.created_by': 'Private report widgets',
  'report_definitions.created_by': 'Private saved reports',
  'group_members.source': 'Group seats held through a role',
  'group_members.kept_manual': "Kept account's group seats made manual",
  'goal_progress.performed_by_id': 'Goal progress entries',
  'intervention_progress.performed_by_id': 'Intervention entries',
  'patient_goals.completed_by_id': 'Goals met or unattainable',
  'patient_interventions.completed_by_id': 'Interventions done',
  'episode_notes.created_by_id': 'Episode notes',
};

function accountName(account: AccountMergeUser): string {
  const name = [account.last_name, account.first_name]
    .filter(Boolean)
    .join(', ');
  return name || account.email || 'Unnamed account';
}

function candidateLabel(candidate: AccountMergeCandidate): string {
  const { account } = candidate;
  const label = `${accountName(account)} · ${account.email ?? 'no e-mail'}`;
  if (candidate.merged_away) return `${label} (merged)`;
  if (candidate.membership_state === null) return `${label} (not a member)`;
  if (candidate.membership_state === 'deactivated') {
    return `${label} (deactivated)`;
  }
  return label;
}

function toOption(candidate: AccountMergeCandidate) {
  return { value: candidate.account.id, label: candidateLabel(candidate) };
}

/** "visits.assigned_clinician_id" reads as "visits: assigned clinician". */
function countLabel(key: string): string {
  const [table, column] = key.split('.');
  if (column === undefined) return KEPT_LABELS[key] ?? key.replace(/_/g, ' ');
  const what = column.replace(/_id$/, '').replace(/_/g, ' ');
  return ACCOUNT_LABELS[key] ?? `${table.replace(/_/g, ' ')}: ${what}`;
}

function sameAddress(stored: string | null, typed: string): boolean {
  return (
    stored !== null &&
    stored.trim().toLowerCase() === typed.trim().toLowerCase()
  );
}

function stepsPending(merge: AccountMergeRecord): boolean {
  return (
    merge.workos_status === 'pending' || merge.rogerdoc_status === 'pending'
  );
}

function clinics(n: number): string {
  return `${n} clinic${n === 1 ? '' : 's'}`;
}

function nonZero(counts: Record<string, number>): [string, number][] {
  return Object.entries(counts).filter(([, n]) => n > 0);
}

/** A refusal changed nothing; any other failure may have come after the commit. */
function wasRefused(result: unknown): boolean {
  const status = getHttpStatus(result);
  return status !== undefined && status < 500;
}

/** The call's data, or the message to show after logging it under `label`. */
function readResult<T>(
  result: { data?: T; error?: unknown },
  label: string,
  fallback: string
): T | string {
  if (result.error || result.data === undefined) {
    console.error(`${label}:`, result.error);
    return getUserFacingApiMessage(result, fallback);
  }
  return result.data;
}

function historyLine(merge: AccountMergeRecord): string {
  const parts = [`Merged ${formatDateTimeInLocalZone(merge.created_at)}`];
  if (merge.reversed_at) {
    parts.push(`reversed ${formatDateTimeInLocalZone(merge.reversed_at)}`);
  }
  if (stepsPending(merge)) parts.push('steps pending');
  return parts.join(' · ');
}

const AccountSummary: React.FC<{
  title: string;
  account: AccountMergeUser;
}> = ({ title, account }) => (
  <div className='rounded-md border p-3 text-sm min-w-0'>
    <p className='text-xs font-medium text-muted-foreground'>{title}</p>
    <p className='font-medium break-words'>{accountName(account)}</p>
    <p className='text-muted-foreground break-all'>
      {account.email ?? 'No e-mail'}
    </p>
    <p className='text-xs text-muted-foreground mt-1'>
      Created {formatDateTimeInLocalZone(account.created_at)}
    </p>
    <p className='text-xs text-muted-foreground'>
      {account.last_login
        ? `Last signed in ${formatDateTimeInLocalZone(account.last_login)}`
        : 'Never signed in'}
    </p>
  </div>
);

const CountList: React.FC<{ counts: [string, number][] }> = ({ counts }) => (
  <ul className='text-sm space-y-0.5'>
    {counts.map(([key, n]) => (
      <li key={key} className='flex justify-between gap-3'>
        <span className='text-muted-foreground'>{countLabel(key)}</span>
        <span className='tabular-nums'>{n}</span>
      </li>
    ))}
  </ul>
);

const DebugCounts: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className='space-y-1'>
    <div className='flex flex-wrap items-center gap-2'>
      <p className='text-sm font-medium'>{title}</p>
      <Badge variant='secondary' className='whitespace-nowrap'>
        Debugger only view
      </Badge>
    </div>
    {children}
  </div>
);

const MergeAccountDialog: React.FC<MergeAccountDialogProps> = ({
  scope,
  keepUserId,
  onClose,
  onAccountsChanged,
}) => {
  const { showError, showSuccess, hasPermission } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
      hasPermission: state.hasPermission,
    }))
  );
  // The per-table counts are for whoever debugs a merge, not for the admin.
  const canDebug = hasPermission(PermissionSlug.DEBUG_ACCESS);
  const open = scope !== null;
  const agency = scope !== null && scope !== 'own-agency' ? scope.agency : null;
  const agencyId = agency?.id ?? null;
  const api = useMemo(
    () => (!open ? null : agencyId ? agencyApi(agencyId) : OWN_AGENCY_API),
    [open, agencyId]
  );

  const [candidates, setCandidates] = useState<AccountMergeCandidate[] | null>(
    null
  );
  const [history, setHistory] = useState<AccountMergeHistory | 'failed' | null>(
    null
  );
  const [sourceId, setSourceId] = useState('');
  const [targetId, setTargetId] = useState(keepUserId ?? '');
  const [step, setStep] = useState<Step>({ kind: 'pick' });
  const [typedEmail, setTypedEmail] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadHistory = useCallback(async () => {
    if (!api?.history) return;
    const loaded = readResult(
      await api.history(),
      'Failed to load past merges',
      'Past merges could not be loaded.'
    );
    setHistory(typeof loaded === 'string' ? 'failed' : loaded);
  }, [api]);

  const loadCandidates = useCallback(async () => {
    if (!api) return;
    const loaded = readResult(
      await api.candidates(),
      'Failed to load merge candidates',
      'Unable to load accounts. Please try again.'
    );
    if (typeof loaded === 'string') {
      showError('Failed to Load Accounts', loaded);
      setCandidates(prev => prev ?? []);
      return;
    }
    setCandidates(loaded);
  }, [api, showError]);

  // The parent mounts a fresh dialog per scope, so there is no state to reset.
  useEffect(() => {
    void loadCandidates();
    void loadHistory();
  }, [loadCandidates, loadHistory]);

  const sourceOptions = useMemo(
    () => (candidates ?? []).map(toOption),
    [candidates]
  );
  // Only a live member who signs in by e-mail can be kept.
  const targetOptions = useMemo(
    () =>
      (candidates ?? [])
        .filter(c => c.can_be_kept && c.account.id !== sourceId)
        .map(toOption),
    [candidates, sourceId]
  );
  // A pre-filled account counts only once it is on offer to keep.
  const keptId = targetOptions.some(o => o.value === targetId) ? targetId : '';
  const accountsById = useMemo(() => {
    const byId = new Map<string, AccountMergeUser>();
    for (const c of candidates ?? []) byId.set(c.account.id, c.account);
    if (history && history !== 'failed') {
      for (const account of history.accounts) byId.set(account.id, account);
    }
    return byId;
  }, [candidates, history]);

  const fetchPreview = async (
    mergeApi: MergeApi,
    sourceUserId: string,
    targetUserId: string
  ): Promise<AccountMergePreview | string> =>
    readResult(
      await mergeApi.preview(sourceUserId, targetUserId),
      'Failed to preview merge',
      'Unable to preview the merge. Please try again.'
    );

  const showPreview = (preview: AccountMergePreview) => {
    setTypedEmail('');
    setStep({ kind: 'preview', preview });
  };

  const replaceInHistory = (merge: AccountMergeRecord) =>
    setHistory(prev =>
      prev && prev !== 'failed'
        ? {
            ...prev,
            merges: prev.merges.map(m => (m.id === merge.id ? merge : m)),
          }
        : prev
    );

  const refreshAfterChange = async () => {
    onAccountsChanged();
    await Promise.all([loadHistory(), loadCandidates()]);
  };

  const handlePreview = async () => {
    if (!api) return;
    setBusy(true);
    setNotice(null);
    try {
      const found = await fetchPreview(api, sourceId, keptId);
      if (typeof found === 'string') {
        showError('Cannot Merge These Accounts', found);
        return;
      }
      showPreview(found);
    } finally {
      setBusy(false);
    }
  };

  const handleMerge = async () => {
    if (!api || step.kind !== 'preview') return;
    const { source, target, token } = step.preview;
    setBusy(true);
    try {
      const result = await api.merge({
        source_user_id: source.id,
        target_user_id: target.id,
        preview_token: token,
        confirm_target_email: typedEmail,
      });
      const merged = readResult(
        result,
        'Failed to merge accounts',
        'Unable to merge the accounts. Please try again.'
      );
      if (typeof merged !== 'string') {
        setStep({ kind: 'done', merge: merged });
        if (!stepsPending(merged)) showSuccess('Accounts merged.');
        await refreshAfterChange();
        return;
      }
      const refused = wasRefused(result);
      if (!refused) await refreshAfterChange();
      const message = refused ? merged : UNCONFIRMED;
      // Show the accounts as they are now, so a stale plan cannot be confirmed.
      const found = await fetchPreview(api, source.id, target.id);
      if (typeof found === 'string') {
        setStep({ kind: 'pick' });
        if (refused) showError('Accounts Not Merged', message);
        else {
          showError(
            'Merge Not Confirmed',
            agency ? UNCONFIRMED_PICK : UNCONFIRMED_PICK_OWN
          );
        }
        return;
      }
      showPreview(found);
      setNotice(message);
    } finally {
      setBusy(false);
    }
  };

  const handleRetry = async (merge: AccountMergeRecord) => {
    if (!api) return;
    setBusy(true);
    try {
      const updated = readResult(
        await api.retry(merge.id),
        'Failed to retry merge steps',
        'Unable to finish the remaining steps. Please try again.'
      );
      if (typeof updated === 'string') {
        showError('Steps Still Pending', updated);
        await loadHistory();
        return;
      }
      replaceInHistory(updated);
      setStep(prev =>
        prev.kind === 'done' && prev.merge.id === updated.id
          ? { ...prev, merge: updated }
          : prev
      );
      if (stepsPending(updated)) {
        showError(
          'Steps Still Pending',
          updated.post_commit_error ?? STEPS_PENDING
        );
      } else {
        showSuccess('Remaining steps finished.');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleReverse = async () => {
    if (!api?.reverse || step.kind !== 'confirmReverse') return;
    setBusy(true);
    try {
      const result = await api.reverse(step.merge.id);
      const reversed = readResult(
        result,
        'Failed to reverse merge',
        'Unable to reverse the merge. Please try again.'
      );
      if (typeof reversed === 'string') {
        setStep({ kind: 'pick' });
        if (wasRefused(result)) {
          showError('Merge Not Reversed', reversed);
          await loadHistory();
        } else {
          showError('Reversal Not Confirmed', REVERSAL_UNCONFIRMED);
          await refreshAfterChange();
        }
        return;
      }
      const { merge, ...reversal } = reversed;
      setStep({ kind: 'done', merge, reversal });
      if (!stepsPending(merge)) showSuccess('Merge reversed.');
      await refreshAfterChange();
    } finally {
      setBusy(false);
    }
  };

  const preview = step.kind === 'preview' ? step.preview : null;
  const canMerge =
    preview !== null &&
    preview.blocks.length === 0 &&
    sameAddress(preview.target.email, typedEmail);
  const pastMerges = history && history !== 'failed' ? history.merges : [];
  const reverseSource =
    step.kind === 'confirmReverse'
      ? accountsById.get(step.merge.source_user_id)
      : undefined;
  const reverseTarget =
    step.kind === 'confirmReverse'
      ? accountsById.get(step.merge.target_user_id)
      : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next && !busy) onClose();
      }}
    >
      <DialogContent className='max-w-xl max-h-[90vh] overflow-y-auto grid-cols-1'>
        <DialogHeader>
          <DialogTitle>Merge duplicate accounts</DialogTitle>
          <DialogDescription>
            {agency ? `${agency.written_name}: move` : 'Move'} a duplicate
            account&apos;s assignments and Kinnser logins to the account the
            person uses.
          </DialogDescription>
        </DialogHeader>

        {step.kind === 'pick' && (
          <div className='space-y-4'>
            <SamePersonNote />
            <div className='space-y-1.5'>
              <Label htmlFor='merge-source'>Duplicate account</Label>
              <VirtualizedCombobox
                id='merge-source'
                options={sourceOptions}
                value={sourceId}
                onValueChange={value => {
                  setSourceId(value);
                  if (value === targetId) setTargetId('');
                }}
                placeholder='Choose the account to merge away'
                searchPlaceholder='Search by name or e-mail'
                isLoading={candidates === null}
                disabled={busy}
                className='w-full'
                buttonClassName='w-full'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='merge-target'>Account to keep</Label>
              <VirtualizedCombobox
                id='merge-target'
                options={targetOptions}
                value={keptId}
                onValueChange={setTargetId}
                placeholder='Choose the account the person uses'
                searchPlaceholder='Search by name or e-mail'
                isLoading={candidates === null}
                disabled={busy}
                className='w-full'
                buttonClassName='w-full'
              />
              <p className='text-xs text-muted-foreground'>
                Only active members with an e-mail can be kept.
              </p>
            </div>
            {history === 'failed' && (
              <p className='text-sm text-muted-foreground'>
                Past merges could not be loaded.
              </p>
            )}
            {pastMerges.length > 0 && (
              <div className='space-y-2'>
                <p className='text-sm font-medium'>Past merges</p>
                <ul className='space-y-2'>
                  {pastMerges.map(merge => {
                    const source = accountsById.get(merge.source_user_id);
                    const target = accountsById.get(merge.target_user_id);
                    return (
                      <li
                        key={merge.id}
                        className='rounded-md border p-3 text-sm space-y-2'
                      >
                        <div className='min-w-0'>
                          <p className='font-medium break-words'>
                            {source ? accountName(source) : 'Unknown account'} →{' '}
                            {target ? accountName(target) : 'Unknown account'}
                          </p>
                          <p className='text-muted-foreground break-all'>
                            {source?.email ?? 'No e-mail'} →{' '}
                            {target?.email ?? 'No e-mail'}
                          </p>
                          <p className='text-xs text-muted-foreground'>
                            {historyLine(merge)}
                          </p>
                        </div>
                        {(stepsPending(merge) ||
                          merge.status === 'completed') && (
                          <div className='flex flex-wrap gap-2'>
                            {stepsPending(merge) && (
                              <Button
                                size='sm'
                                variant='outline'
                                onClick={() => handleRetry(merge)}
                                disabled={busy}
                              >
                                Retry
                              </Button>
                            )}
                            {merge.status === 'completed' && (
                              <Button
                                size='sm'
                                variant='outline'
                                onClick={() =>
                                  setStep({
                                    kind: 'confirmReverse',
                                    merge,
                                    back: { kind: 'pick' },
                                  })
                                }
                                disabled={busy}
                              >
                                Reverse
                              </Button>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}

        {preview && (
          <div className='space-y-4'>
            {notice && (
              <Alert>
                <AlertDescription>{notice}</AlertDescription>
              </Alert>
            )}
            <div className='grid gap-3 md:grid-cols-2'>
              <AccountSummary title='Merged away' account={preview.source} />
              <AccountSummary title='Kept' account={preview.target} />
            </div>
            {preview.rerun_of && (
              <p className='text-sm text-muted-foreground'>
                These accounts were merged before. Merging again moves anything
                assigned to the old account since.
              </p>
            )}
            {canDebug && (
              <DebugCounts title='Moves to the kept account'>
                {nonZero(preview.counts).length > 0 ? (
                  <CountList counts={nonZero(preview.counts)} />
                ) : (
                  <p className='text-sm text-muted-foreground'>
                    Nothing is assigned to the old account.
                  </p>
                )}
              </DebugCounts>
            )}
            {preview.clinics.some(c => c.kinnser_user_key) && (
              <div className='space-y-1'>
                <p className='text-sm font-medium'>Kinnser logins</p>
                <ul className='text-sm space-y-0.5'>
                  {preview.clinics
                    .filter(c => c.kinnser_user_key)
                    .map(c => (
                      <li key={c.clinic_id}>
                        {getAgencyClinicDisplayName({
                          id: c.clinic_id,
                          name: c.clinic_name,
                          clinic_key: c.clinic_key,
                        })}
                        : {c.kinnser_user_key}
                      </li>
                    ))}
                </ul>
              </div>
            )}
            {nonZero(preview.kept_counts).length > 0 && (
              <div className='space-y-1'>
                <p className='text-sm font-medium'>
                  Stays with the old account
                </p>
                <CountList counts={nonZero(preview.kept_counts)} />
              </div>
            )}
            {preview.roles_revoked.length > 0 && (
              <p className='text-sm'>
                <span className='font-medium'>Roles removed: </span>
                {preview.roles_revoked.join(', ')}
              </p>
            )}
            {preview.warnings.map((warning, i) => (
              <Alert key={i}>
                <AlertDescription>{warning}</AlertDescription>
              </Alert>
            ))}
            {preview.blocks.map((block, i) => (
              <Alert key={i} variant='destructive'>
                <AlertDescription>{block}</AlertDescription>
              </Alert>
            ))}
            <SamePersonNote />
            {preview.blocks.length === 0 && (
              <div className='space-y-1.5'>
                <Label htmlFor='merge-confirm' className='leading-snug'>
                  Type{' '}
                  <span className='font-mono break-all'>
                    {preview.target.email}
                  </span>{' '}
                  to confirm
                </Label>
                <Input
                  id='merge-confirm'
                  value={typedEmail}
                  onChange={event => setTypedEmail(event.target.value)}
                  autoComplete='off'
                />
              </div>
            )}
          </div>
        )}

        {step.kind === 'confirmReverse' && (
          <div className='space-y-4'>
            <div className='grid gap-3 md:grid-cols-2'>
              {reverseSource && (
                <AccountSummary title='Merged away' account={reverseSource} />
              )}
              {reverseTarget && (
                <AccountSummary title='Kept' account={reverseTarget} />
              )}
            </div>
            {canDebug && nonZero(step.merge.counts).length > 0 && (
              <div className='space-y-1'>
                <p className='text-sm font-medium'>Moved by the merge</p>
                <CountList counts={nonZero(step.merge.counts)} />
              </div>
            )}
            <p className='text-sm'>
              Assignments and Kinnser logins go back to the merged-away account.
              Anything reassigned since the merge stays where it is. Its roles
              are not restored.
            </p>
            <Alert variant='destructive'>
              <AlertDescription>
                The reversal cannot be undone. You can merge these two accounts
                again later.
              </AlertDescription>
            </Alert>
          </div>
        )}

        {step.kind === 'done' && (
          <div className='space-y-3'>
            <p className='text-sm'>
              {step.merge.status === 'completed'
                ? 'The accounts are merged.'
                : 'The merge is reversed.'}
            </p>
            {step.reversal ? (
              <>
                {Object.keys(step.reversal.reassigned_since).length > 0 && (
                  <div className='space-y-1'>
                    <p className='text-sm font-medium'>
                      Stayed with the kept account (reassigned since the merge)
                    </p>
                    <CountList
                      counts={Object.entries(
                        step.reversal.reassigned_since
                      ).map(([key, ids]) => [key, ids.length])}
                    />
                  </div>
                )}
                {step.reversal.keys_kept.length > 0 && (
                  <p className='text-sm'>
                    Kinnser logins stayed where they are at{' '}
                    {clinics(step.reversal.keys_kept.length)}, because they
                    moved on since the merge.
                  </p>
                )}
                {step.reversal.clinics_gone.length > 0 && (
                  <p className='text-sm'>
                    Nothing went back to{' '}
                    {clinics(step.reversal.clinics_gone.length)} whose access
                    rows are gone.
                  </p>
                )}
              </>
            ) : (
              canDebug && (
                <DebugCounts title='Moved to the kept account'>
                  <CountList counts={nonZero(step.merge.counts)} />
                </DebugCounts>
              )
            )}
            {stepsPending(step.merge) && (
              <Alert variant='destructive'>
                <AlertDescription>
                  {step.merge.post_commit_error ?? STEPS_PENDING}
                </AlertDescription>
              </Alert>
            )}
            {stepsPending(step.merge) && !agency && (
              <p className='text-sm text-muted-foreground'>
                {FINISH_LATER_OWN}
              </p>
            )}
          </div>
        )}

        <DialogFooter className='gap-2'>
          {step.kind === 'pick' && (
            <>
              <Button variant='outline' onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button
                onClick={handlePreview}
                disabled={busy || !sourceId || !keptId}
              >
                {busy && <Loader2 className='h-4 w-4 animate-spin mr-2' />}
                Preview merge
              </Button>
            </>
          )}
          {(step.kind === 'preview' || step.kind === 'confirmReverse') && (
            <Button
              variant='outline'
              onClick={() => {
                setNotice(null);
                setStep(
                  step.kind === 'confirmReverse' ? step.back : { kind: 'pick' }
                );
              }}
              disabled={busy}
            >
              Back
            </Button>
          )}
          {step.kind === 'preview' && (
            <Button
              variant='destructive'
              onClick={handleMerge}
              disabled={busy || !canMerge}
            >
              {busy && <Loader2 className='h-4 w-4 animate-spin mr-2' />}
              Merge accounts
            </Button>
          )}
          {step.kind === 'confirmReverse' && (
            <Button
              variant='destructive'
              onClick={handleReverse}
              disabled={busy}
            >
              {busy && <Loader2 className='h-4 w-4 animate-spin mr-2' />}
              Reverse merge
            </Button>
          )}
          {step.kind === 'done' && (
            <>
              {stepsPending(step.merge) && (
                <Button
                  variant='outline'
                  onClick={() => handleRetry(step.merge)}
                  disabled={busy}
                >
                  {busy && <Loader2 className='h-4 w-4 animate-spin mr-2' />}
                  Retry
                </Button>
              )}
              {step.merge.status === 'completed' && api?.reverse && (
                <Button
                  variant='outline'
                  onClick={() =>
                    setStep({
                      kind: 'confirmReverse',
                      merge: step.merge,
                      back: step,
                    })
                  }
                  disabled={busy}
                >
                  Reverse merge
                </Button>
              )}
              <Button onClick={onClose} disabled={busy}>
                Close
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MergeAccountDialog;
